import EventBus from '@/event-bus/EventBus'
import useEventsSDKProvider from '@voicenter-team/events-sdk-vue-3'
import { sdkEventTypes } from '@/enum/sdkEvents'
import parseCatch from '@/helpers/handleErrors'
import env from '@/env'
import i18n from '@/i18n'
import useNotifyService from '@/composables/useNotifyService'
import { getActiveToken } from '@/composables/useCurrentUserData'
import StorageLogger from '@/core/services/StorageLogger'

const $notify = useNotifyService

let sdk = null

// Create provider singleton (no Vuex store needed — uses internal reactive stores)
const provider = useEventsSDKProvider({
    voiceBots: { callsRetentionMinutes: 15 },
    logger: { enabled: true }
})

function onNewEvent (eventData) {
    switch (eventData.name) {
        case sdkEventTypes.CONNECT_ERROR:
            EventBus.emit(sdkEventTypes.CONNECT_ERROR)
            break
        case sdkEventTypes.CONNECT_TIMEOUT:
            EventBus.emit(sdkEventTypes.CONNECT_TIMEOUT)
            break
        default:
            break
    }
}

export default async function initRealTimeSdk () {
    try {
        if (sdk) {
            sdk.emit(sdkEventTypes.CLOSE)
            sdk = null
        }

        let config = {
            isNewStack: true,
            useLogger: true,
            servers: env.VITE_APP_EVENTS_SDK_SERVERS,
            loginType: 'Token',
            token: getActiveToken(),
            serverFetchStrategy: env.VITE_APP_EVENTS_SERVER_FETCH_STRATEGY,
            getSettingsUrl: env.VITE_APP_EVENTS_SDK_SETTINGS_URL,
            loginUrl: env.VITE_APP_EVENTS_SDK_LOGIN_URL,
            refreshTokenUrl: env.VITE_APP_EVENTS_SDK_REFRESH_TOKEN_URL,
            storageLoggerInstance: StorageLogger
        }

        let newOptions = {}

        if (env.VITE_APP_EVENTS_SDK_OPTIONS) {
            try {
                newOptions = JSON.parse(env.VITE_APP_EVENTS_SDK_OPTIONS)
            } catch (e) {
                console.warn('Invalid sdk options format provided via env config')
            }
        }

        config = {
            ...config,
            ...newOptions,
        }

        sdk = await provider.init(config)
        sdk.on('*', onNewEvent)
    } catch (e) {
        parseCatch(e, true)
    }
}

export async function reSyncSdk () {
    if (!sdk) {
        return
    }
    await initRealTimeSdk()
}

export async function setToken (token) {
    if (!sdk) {
        return
    }
    await sdk.setToken(token)
}

export async function subscribeToBotCall (botId, ivrId) {
    await sdk.emit('subscribeVoicebotCallUpdates', {
        voicebotID: botId,
        IvrUniqueID: ivrId
    })
}

export async function unSubscribeFromABotCall (botId, ivrId) {
    await sdk.emit('unsubscribeVoicebotCallUpdates', {
        voicebotID: botId,
        IvrUniqueID: ivrId
    })
}

export async function retrySocketConnection () {
    $notify.add({
        type: 'error',
        title: i18n.global.t('errors.realtime.connection.title'),
        message: i18n.global.t('common.socketAttemptSync'),
    })

    await reSyncSdk()

    $notify.add({
        type: 'success',
        title: i18n.global.t('RealTime SDK'),
        message: i18n.global.t('common.socketSynced'),
    })
}

/** Expose the provider for advanced usage (e.g., connection status) */
export { provider }
